import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
} from 'react';

import { getTextSummary } from '../../api/build-a-dash-ai-gen-api';
import { useFilterContext } from '../../filters/contexts/FilterProvider';
import { useOptionalReportDocument } from '../../report-document/report-document-context';
import { ChartProvider } from '../Chart/ChartProvider';
import { ChartMenuDataProvider } from '../Chart/chart-menu/ChartMenuContext';
import type { ChartExportInfo } from '../Chart/chart-menu/chart-export-types';
import {
  buildOrderedPptExportCharts,
  countAuxiliaryPptBlocking,
  pageHasTextOrAiSummaryItems,
} from '../Chart/chart-menu/ordered-ppt-export-charts';
import { usePageExport } from '../Chart/chart-menu/PageExportProvider';
import { TextChartCardContentWithFilters } from '../Chart/TextChart/TextChartCardContent';
import { getPrimaryParameterSelections } from '../Chart/utils/get-primary-parameter-selections';
import { getExportTitle } from '../Chart/utils/getFileName';
import { MultiMetricDataProvider } from '../DataProvider/DataProvider';
import {
  DEFAULT_GRID_COLUMNS,
  DEFAULT_GRID_ROWS,
} from '../PageConfigProvider/constants';
import {
  usePageConfig,
  useTabContentConfig,
} from '../PageConfigProvider/PageConfigProvider';
import { PageType } from '../PageConfigProvider/types';
import type { PositionedItem } from '../PageConfigProvider/types/item';
import { layoutReportFlowForExport } from '../ReportFlow/report-flow-export-layout';
import { useCoverSelectedParameters } from '../Reports/useCoverSelectedParameters';
import { useTextChartContent } from './TextChartContentProvider';
import styles from './text-chart-orchestrator.module.css';

const useAiSummaryGenerator = () => {
  const { items } = useTabContentConfig();
  const { textCharts, getAllTextCharts, setAiSummary } = useTextChartContent();

  const previousContentHashRef = useRef<string | null>(null);

  const contentStateKey = useMemo(() => {
    const entries = Array.from(textCharts.entries()).map(([id, content]) => ({
      id,
      hasContent: content.content !== null,
      isLoading: content.isLoading,
      contentLength: content.content?.length ?? 0,
    }));
    return JSON.stringify(entries);
  }, [textCharts]);

  // biome-ignore lint/correctness/useExhaustiveDependencies: migrated from develop
  useEffect(() => {
    const hasAiSummaryChart = items.some(
      (item) => item.plot?.chartType === 'ai-summary'
    );

    if (!hasAiSummaryChart) {
      setAiSummary({
        summary: '',
        loading: false,
        error: null,
      });
      return;
    }

    const expectedTextItems = items.filter(
      (item) => item.plot?.chartType === 'text'
    );

    const expectedNonExcludedItems = expectedTextItems.filter((item) => {
      const shouldExclude = item.plot?.excludeFromExecutiveSummary ?? false;
      return !shouldExclude;
    });

    const textChartsList = getAllTextCharts();
    const chartsById = new Map(
      textChartsList.map((chart) => [chart.chartId, chart])
    );

    if (expectedNonExcludedItems.length === 0) {
      setAiSummary({
        summary:
          'No text charts found on this page. Add text charts to generate an AI summary.',
        loading: false,
        error: null,
      });
      return;
    }

    const allExpectedLoaded = expectedNonExcludedItems.every((item) => {
      const chart = chartsById.get(item.id);
      if (!chart) {
        return false;
      }
      return (
        !chart.content.isLoading &&
        (chart.content.content !== null || chart.content.error !== null)
      );
    });

    if (!allExpectedLoaded) {
      return;
    }

    const fetchSummary = async () => {
      try {
        setAiSummary((prev) => ({ ...prev, loading: true, error: null }));

        const currentTextCharts = getAllTextCharts();

        if (currentTextCharts.length === 0) {
          setAiSummary({
            summary: 'No text components found on this report.',
            loading: false,
            error: null,
          });
          return;
        }

        const itemsById = new Map(items.map((item) => [item.id, item]));

        const includedTexts = currentTextCharts
          .map((chart) => {
            if (chart.content.error) {
              return null;
            }

            const content = chart.content.content;
            if (!content || content.trim().length === 0) {
              return null;
            }

            const item = itemsById.get(chart.chartId);
            const shouldExclude =
              item?.plot?.excludeFromExecutiveSummary ?? false;

            if (shouldExclude) {
              return null;
            }

            return content;
          })
          .filter((text): text is string => text !== null);

        if (includedTexts.length === 0) {
          setAiSummary({
            summary: 'No text content found in text components.',
            loading: false,
            error: null,
          });
          return;
        }

        const contentHash = includedTexts.join('|||');
        if (contentHash === previousContentHashRef.current) {
          setAiSummary((prev) => ({ ...prev, loading: false }));
          return;
        }
        previousContentHashRef.current = contentHash;

        const combinedText = includedTexts.join('\n\n');

        const response = await getTextSummary({
          text: combinedText,
        });

        setAiSummary({
          summary: response.summary,
          loading: false,
          error: null,
        });
      } catch (err) {
        setAiSummary({
          summary: '',
          loading: false,
          error:
            err instanceof Error ? err.message : 'Failed to generate summary',
        });
      }
    };

    void fetchSummary();
  }, [getAllTextCharts, items, contentStateKey, setAiSummary]);
};

const useTextChartPptSync = () => {
  const { config, items, primaryFilter } = useTabContentConfig();
  const { config: pageConfig } = usePageConfig();
  const { textCharts, aiSummary } = useTextChartContent();
  const {
    setPptExportResolver,
    setPptAuxiliaryBlockingCount,
    setPageHasTextOrAiCharts,
    setReportExportMetadata,
    getChartRegistrySnapshot,
  } = usePageExport();
  const coverParameters = useCoverSelectedParameters();
  const { activeFilterMap } = useFilterContext();
  const reportParameters = config.reportParameters;
  const primarySelections = useMemo(
    () =>
      getPrimaryParameterSelections({
        reportParameters: reportParameters ?? [],
        activeFilterMap,
      }),
    [reportParameters, activeFilterMap]
  );

  const reportDocumentContext = useOptionalReportDocument();
  const flowDocument = reportDocumentContext?.flow;
  const isReport = pageConfig.type === PageType.REPORT;
  const flowPages = reportDocumentContext?.document.pages;
  const flowExport = useMemo(
    () => (flowPages ? layoutReportFlowForExport(flowPages) : null),
    [flowPages]
  );
  const layoutItems = flowExport?.items ?? items;
  const reportTitle = config.title || pageConfig.name || 'Report';
  const reportDescription = config.description;
  const gridRows =
    flowExport?.gridRows ?? config.grid?.rows ?? DEFAULT_GRID_ROWS;
  const gridColumns = flowExport
    ? DEFAULT_GRID_COLUMNS
    : (config.grid?.columns ?? DEFAULT_GRID_COLUMNS);

  const getTextExportForItem = useCallback(
    (item: PositionedItem): ChartExportInfo | null => {
      const html = item.plot?.textHtml?.trim();
      if (!html) {
        return null;
      }
      const entry = textCharts.get(item.id);
      if (!entry || entry.isLoading || entry.error !== null) {
        return null;
      }
      const text = entry.content?.trim() ?? '';
      if (!text.length) {
        return null;
      }
      const chartTitle = item.title ?? 'Text';
      return {
        title: chartTitle,
        exportTitle: getExportTitle({
          primaryFilterId: primaryFilter?.id,
          pageName: pageConfig.name,
        }),
        dataType: 'text',
        textContent: entry.content ?? '',
      };
    },
    [textCharts, primaryFilter?.id, pageConfig.name]
  );

  const getAiExportForItem = useCallback(
    (item: PositionedItem, summaryBody: string): ChartExportInfo | null => {
      const title = item.title ?? 'Executive Summary';
      const html = item.plot?.textHtml?.trim();
      const textContent = html ? `${html}\n\n${summaryBody}` : summaryBody;
      if (!textContent.trim()) {
        return null;
      }
      return {
        title,
        exportTitle: getExportTitle({
          primaryFilterId: primaryFilter?.id,
          pageName: pageConfig.name,
        }),
        dataType: 'text',
        textContent,
      };
    },
    [primaryFilter?.id, pageConfig.name]
  );

  const pageHasTextOrAiCharts = pageHasTextOrAiSummaryItems(layoutItems);
  const pptAuxiliaryBlockingCount = countAuxiliaryPptBlocking({
    items: layoutItems,
    textCharts,
    aiSummaryState: { loading: aiSummary.loading },
  });

  // Keyed on the values alone: the resolver effect below re-runs on nearly
  // every render, and re-setting provider state to an unchanged value still
  // forces a synchronous provider re-render whenever it has other updates
  // queued. Those renders count towards React's "Maximum update depth" limit.
  useLayoutEffect(() => {
    setPageHasTextOrAiCharts(pageHasTextOrAiCharts);
  }, [pageHasTextOrAiCharts, setPageHasTextOrAiCharts]);

  useLayoutEffect(() => {
    setPptAuxiliaryBlockingCount(pptAuxiliaryBlockingCount);
  }, [pptAuxiliaryBlockingCount, setPptAuxiliaryBlockingCount]);

  useLayoutEffect(() => {
    setPptExportResolver(() => {
      return buildOrderedPptExportCharts({
        items: layoutItems,
        registry: getChartRegistrySnapshot(),
        aiSummaryState: aiSummary,
        getTextExportForItem,
        getAiExportForItem,
      });
    });

    if (isReport) {
      setReportExportMetadata({
        reportTitle,
        reportDescription,
        coverParameters,
        gridColumns: gridColumns || DEFAULT_GRID_COLUMNS,
        gridRows: gridRows || DEFAULT_GRID_ROWS,
        pageGridRows: DEFAULT_GRID_ROWS,
        flow: flowDocument,
        primarySelections,
      });
    } else {
      setReportExportMetadata(null);
    }

    return () => {
      setPptExportResolver(null);
      setReportExportMetadata(null);
    };
  }, [
    aiSummary,
    coverParameters,
    flowDocument,
    getAiExportForItem,
    getChartRegistrySnapshot,
    getTextExportForItem,
    gridColumns,
    gridRows,
    isReport,
    layoutItems,
    primarySelections,
    reportDescription,
    reportTitle,
    setPptExportResolver,
    setReportExportMetadata,
  ]);
};

const OffScreenTextChartsHydration = () => {
  const { items, primaryFilter } = useTabContentConfig();
  const { config: pageConfig } = usePageConfig();

  const textItems = items.filter(
    (item): item is PositionedItem => item.plot?.chartType === 'text'
  );

  if (textItems.length === 0) {
    return null;
  }

  return (
    <div className={styles.offScreenHydration} aria-hidden>
      {textItems.map((item) => (
        <ChartProvider key={item.id} item={item}>
          <ChartMenuDataProvider>
            <MultiMetricDataProvider
              item={item}
              chartType="text"
              primaryDimension={primaryFilter?.id}
            >
              {({ data, status }) => (
                <TextChartCardContentWithFilters
                  data={data}
                  status={status}
                  item={item}
                  pageConfig={pageConfig}
                />
              )}
            </MultiMetricDataProvider>
          </ChartMenuDataProvider>
        </ChartProvider>
      ))}
    </div>
  );
};

export const TextChartOrchestrator = () => {
  useAiSummaryGenerator();
  useTextChartPptSync();

  return <OffScreenTextChartsHydration />;
};
